import dayjs from 'dayjs'

// Cùng kiểu định dạng số với màn danh sách hóa đơn (EInvoice.js → formatNumber): 1.500.000
const numberFormatter = new Intl.NumberFormat('de-US')

export default {
  name: 'LogTable',
  props: {
    logData: { type: Array, required: true },
    logStatus: { type: Object, required: true },
  },
  methods: {
    onToggleRow(log) {
      this.$emit('toggle-row', log)
    },

    isFailed(log) {
      return log && log.State === 'Failed'
    },

    /**
     * Tiêu đề khối đối tượng. Một hóa đơn có nhiều dòng hàng hóa ⇒ nhiều khối cùng tên; đánh số thứ tự để
     * người đọc bám được "dòng thứ mấy" (thao tác sửa chỉ ghi các trường đổi, không có tên hàng để nhận ra).
     */
    getSectionTitle(log, detail) {
      const label = this.getEntityLabel(detail)
      const sameEntity = (log.Details || []).filter(d => d.Entity === detail.Entity)
      if (sameEntity.length < 2) return label
      return `${label} (${sameEntity.indexOf(detail) + 1})`
    },

    isBlank(value) {
      return value === null || value === undefined || value === ''
    },

    /**
     * Giá trị rỗng để trắng, không in chữ "(trống)" (tester 29/09: đồng nhất với các ô không có giá trị khác — vd
     * ngắt kết nối nhà cung cấp đưa mọi cột về rỗng thì cả cột giá trị mới trống trơn).
     */
    displayValue(field, value) {
      if (this.isBlank(value)) return ''
      return this.formatValue(field, value)
    },

    /** Nhãn trường: ưu tiên nhãn backend đã dịch; module chưa khai danh mục thì tra khóa dịch theo tên cột. */
    getFieldLabel(field) {
      if (field.Label) return field.Label
      return this.translateOrRaw(field.Field)
    },

    getEntityLabel(detail) {
      if (detail.EntityName) return detail.EntityName
      return this.translateOrRaw(detail.Entity)
    },

    /** Không có khóa dịch thì hiện tên gốc chứ không để trống. */
    translateOrRaw(key) {
      if (key === null || key === undefined) return ''
      const k = String(key).trim()
      const label = this.$t(k)
      return label === k ? k : label
    },

    /** Lớp dải màu trạng thái (thêm / sửa / xóa) — cùng màu với chú thích phía trên bảng. */
    getEntityStateClass(state) {
      switch (state) {
        case 'Added': return this.logStatus.ADDED.class
        case 'Deleted': return this.logStatus.DELETED.class
        case 'Modified': return this.logStatus.CHANGED.class
        default: return ''
      }
    },

    /**
     * Định dạng theo KIỂU backend khai cho trường, không đoán theo nội dung: số điện thoại, CCCD, số tài khoản
     * cũng toàn chữ số, đoán "trông giống số" là định dạng nhầm thành 912.345.678.
     */
    formatValue(field, value) {
      switch (field.Kind) {
        case 'Number': {
          const number = Number(value)
          return Number.isFinite(number) ? numberFormatter.format(number) : value
        }

        case 'WallClockDateTime': {
          // Ngày hóa đơn lưu theo GIỜ KHÁCH SẠN: in đúng giờ đã lưu, bỏ phần múi giờ (nếu có) trước khi đọc —
          // quy đổi theo máy người xem là lệch so với màn danh sách hóa đơn.
          const wallClock = String(value).slice(0, 19)
          const date = dayjs(wallClock)
          return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : value
        }

        case 'UtcDateTime': {
          // Lưu theo giờ UTC (ngày mời, ngày hết hạn token) ⇒ quy về giờ người xem. KHÔNG dựa vào hậu tố "Z":
          // giá trị đọc lại từ DB mất Kind nên thường không có, cứ coi phần ngày giờ là UTC.
          const utc = new Date(`${String(value).slice(0, 19)}Z`)
          return Number.isNaN(utc.getTime()) ? value : dayjs(utc).format('DD/MM/YYYY HH:mm')
        }

        case 'TranslationKey':
          // Giá trị là khóa dịch (vd tên vai trò user_group_owner) — tra ở bộ dịch của giao diện.
          return this.translateOrRaw(value)

        default:
          // Trường đã có trong danh mục: giá trị mã đã được backend dịch sẵn, in nguyên văn.
          if (field.Label) return value
          return this.formatLegacyValue(value, field.Field)
      }
    },

    /**
     * Trường của module chưa khai danh mục: giữ cách hiển thị cũ — có khóa dịch cho cặp trường_giá-trị thì dùng,
     * còn ngày giờ thì quy đổi về múi giờ của người xem.
     * ⚠️ Cố tình KHÔNG cộng thêm 7 giờ như bản cũ: backend luôn ghi giờ UTC kèm hậu tố múi giờ, cộng tay
     * là sai với người dùng ở múi giờ khác và sai luôn khi trình duyệt đã tự quy đổi.
     */
    formatLegacyValue(value, key) {
      const keyValue = `${key}_${value}`
      const translated = this.$t(keyValue)
      if (translated !== keyValue) return translated

      if (dayjs(value).isValid() && /^\d{4}-\d{2}-\d{2}T/.test(String(value))) {
        return dayjs(value).format('DD/MM/YYYY HH:mm')
      }

      return value
    },
  },
}
